import { Button, InputSelect } from '@global-ecom/nitro-uds/elements';
import { tw } from 'twind';

import { Link } from 'ui/elements/Link';
import { useTranslate } from 'hooks/useTranslations';
import { AlertMessage } from 'ui/elements/AlertMessage';
import { countingNumbersAsDropdownList } from 'utils/countingNumbers';
import { useFeature } from 'hooks/useFeature';
import { useSiteConfig } from 'hooks/useSiteConfig';

import { AvailabilityBadge, STOCK_MESSAGE_CODE } from '../AvailabilityBadge';
import { Img } from '../Img';
import { Price } from '../Price';
import { ProductPrice } from '../ProductPrice/ProductPrice';
import { RestrictedProductMessage } from '../RestrictedProductMessage';
import { GWPFreeGiftMessage } from '../GiftWithPurchase/GWP.FreeGiftMessage';

import { IShoppingCartItemProps } from './types';

import { ProductInfoRow } from '.';

const ShoppingCartItemPrimaryNested: React.FC<IShoppingCartItemProps> = ({
  item,
  compact,
  preventItemLinks,
  isOffline,
  meta,
  updating,
  removing,
  maxPurchasableQuantity,
  showAlertMessage,
  allowUpdate,
  allowRemove,
  onChangeQuantity,
  basketId,
  totalProductPrice,
  salePrice,
  promotionMessages,
  promotionPrice,
  onEdit,
  onRemove,
  error,
  isPurchased,
  personalizationPrice,
  gwpTitle,
}) => {
  const t = useTranslate();
  const {
    staticFeatures: { showHeaderSubHeaderProductDetail },
  } = useSiteConfig();
  const isOnlineOrPurchased = !isOffline || isPurchased;
  const showOutOfStockMessage =
    meta.outOfStock && meta.adjustQuantityMessage && !showAlertMessage;
  const showPromoExclusionMsgAboveProductDetails = useFeature(
    'SHOW_PROMO_EXCLUSION_MSG_ABOVE_PRODUCT_DETAILS'
  );
  const showPromoExclusionMsgInBaseSize = useFeature(
    'SHOW_PROMO_EXCLUSION_MSG_IN_BASE_SIZE'
  );
  const allowQuantityChange = allowUpdate && !isOffline;
  const header =
    item.__typename === 'ProductCartItem'
      ? item.product.header
      : item.__typename === 'GiftCardCartItem'
      ? meta.name
      : null;
  const subHeader =
    item.__typename === 'ProductCartItem' && item.product.subHeader;

  return (
    <div className={tw('space-y-6 border p-3 xs:p-4', !compact && 'md:p-5')}>
      {item.__typename === 'ProductCartItem' && item.restricted && (
        <RestrictedProductMessage
          plural={false}
          dataTestId="restricted-cart-item-message"
        />
      )}
      {item.__typename === 'ProductCartItem' &&
        !item.restricted &&
        item.bonusItemId && <GWPFreeGiftMessage gwpTitle={gwpTitle} />}
      <div
        className="flex items-center flex-grow"
        data-test-id="shopping-cart-item"
        data-test-item-id={item.id}
      >
        <div className="sm:flex font-display w-full relative">
          <div className="flex items-start flex-grow space-x-4 sm:space-x-6">
            <Link
              href={preventItemLinks ? null : meta.pdpUrl}
              className={tw(
                'relative flex-none flex flex-col',
                isPurchased && 'opacity-100'
              )}
              data-test-id="cart-item-img-link"
              disabled={isOffline}
            >
              <div
                className={tw(
                  'w-24 2xs:w-28 xs:w-32 sm:w-36',
                  !compact && 'sm:w-48'
                )}
              >
                <Img
                  src={meta.imageUrl.replace(/1000/g, '500')}
                  alt={meta.name}
                  width={300}
                />
              </div>
              {item.__typename === 'ProductCartItem' && (
                <AvailabilityBadge
                  className="hidden 2xs:flex absolute bottom-1 left-1"
                  outOfStock={meta.outOfStock}
                  stockMessage={item?.stockMessage}
                  stockStatus={
                    STOCK_MESSAGE_CODE[item?.stockMessageCode ?? 'IN_STOCK']
                  }
                  offline={isOffline}
                />
              )}
            </Link>
            <div className="flex justify-between w-full flex-col gap-4 md:flex-row">
              <div className="space-y-3">
                <Link
                  href={preventItemLinks ? null : meta.pdpUrl}
                  className={tw(
                    'relative flex-none flex flex-col',
                    isPurchased && 'opacity-100'
                  )}
                  data-test-id="cart-product-title-link"
                  disabled={isOffline}
                >
                  <div className="flex flex-col w-full">
                    <h3
                      className={tw(
                        'font-bold text-puma-black cursor-pointer leading-tight!',
                        compact && 'text-sm 2xs:text-base',
                        !compact &&
                          'text-sm 2xs:text-base sm:text-xl lg:text-lg xl:text-xl'
                      )}
                      data-test-id="cart-product-title"
                    >
                      {showHeaderSubHeaderProductDetail ? header : meta.name}
                    </h3>
                    {showHeaderSubHeaderProductDetail && subHeader && (
                      <span
                        className="w-full mobile:text-sm text-base text-puma-gray-60 line-clamp-2"
                        data-test-id="cart-product-subheader"
                      >
                        {subHeader}
                      </span>
                    )}
                  </div>
                </Link>
                {item.__typename === 'ProductCartItem' && (
                  <div className="space-y-4">
                    <div>
                      {promotionMessages.map(message => {
                        return (
                          <div
                            data-test-id="cart-item-promotion"
                            key={message}
                            className={tw(
                              'text-puma-red text-xs xs:text-sm flex flex-wrap',
                              !compact && 'md:text-base lg:text-sm xl:text-base'
                            )}
                          >
                            {message}
                          </div>
                        );
                      })}
                      {item.product.promotionExclusion &&
                        showPromoExclusionMsgAboveProductDetails && (
                          <div
                            className={tw(
                              'text-xs xs:text-sm flex flex-wrap uppercase',
                              !compact && showPromoExclusionMsgInBaseSize
                                ? 'text-puma-red md:text-base lg:text-sm xl:text-base'
                                : 'text-puma-gray md:text-xs lg:text-xs xl:text-xs'
                            )}
                          >
                            {t('promotionExclusionCart')}
                          </div>
                        )}
                      <ProductInfoRow
                        testId={'color'}
                        compact={compact}
                        name={t('color')}
                        info={item.product.colorName}
                      />
                      {isOnlineOrPurchased && (
                        <ProductInfoRow
                          testId={'size'}
                          compact={compact}
                          name={t('size')}
                          info={item.product.size.label}
                        />
                      )}
                      {item.customizationName && (
                        <ProductInfoRow
                          testId={'name'}
                          compact={compact}
                          name={t('name')}
                          info={item.customizationName}
                        />
                      )}
                      {item.customizationNumber && (
                        <ProductInfoRow
                          testId={'number'}
                          compact={compact}
                          name={t('number')}
                          info={item.customizationNumber}
                        />
                      )}
                      {item.customizationCost && (
                        <ProductInfoRow
                          testId={'customization'}
                          compact={compact}
                          name={t('customization')}
                          info={
                            <Price amount={parseInt(item.customizationCost)} />
                          }
                        />
                      )}
                      {item.personalizationName && (
                        <ProductInfoRow
                          testId={'name'}
                          compact={compact}
                          name={t('name')}
                          info={item.personalizationName}
                        />
                      )}
                      {item.personalizationNumber && (
                        <ProductInfoRow
                          testId={'number'}
                          compact={compact}
                          name={t('number')}
                          info={item.personalizationNumber}
                        />
                      )}
                      {item.personalizationSurcharge && (
                        <ProductInfoRow
                          testId={'name'}
                          compact={compact}
                          name={t('customizationCost')}
                          info={
                            <Price amount={item.personalizationSurcharge} />
                          }
                        />
                      )}
                      <ProductInfoRow
                        testId={'styleNumber'}
                        compact={compact}
                        name={t('styleNumber')}
                        info={item.product.styleNumber!}
                      />
                    </div>
                    {item.product.promotionExclusion &&
                      !showPromoExclusionMsgAboveProductDetails &&
                      showPromoExclusionMsgInBaseSize && (
                        <div
                          className={tw(
                            'text-xs xs:text-sm flex flex-wrap',
                            !compact && 'md:text-base lg:text-sm xl:text-base'
                          )}
                        >
                          {t('promotionExclusion')}
                        </div>
                      )}
                    {removing || !allowRemove ? (
                      <p
                        data-test-id="quantity"
                        className={tw(
                          'flex items-center space-x-1 text-puma-black text-xs xs:text-sm',
                          !compact && 'md:text-base'
                        )}
                      >
                        <span className="capitalize text-puma-black-300">
                          {t('quantity')}:{' '}
                        </span>
                        {item.quantity}
                      </p>
                    ) : (
                      <div className="flex w-24">
                        <InputSelect
                          id={`quantity-select-${item.id}`}
                          dataTestId="quantity-select"
                          label={t('quantity')}
                          showLabel={false}
                          placeholder={t('qty')}
                          options={countingNumbersAsDropdownList(
                            maxPurchasableQuantity,
                            item.quantity
                          )}
                          value={item.quantity.toString()}
                          onChange={onChangeQuantity}
                          disabled={
                            updating || !basketId || !allowQuantityChange
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
                {showAlertMessage && (
                  <div className="border border-puma-red rounded flex  items-center p-1">
                    <AlertMessage
                      dataTestId="cart-item-alert-message"
                      icon="exclamation-outline"
                      type="warning"
                    >
                      {error ? error : meta.adjustQuantityMessage}
                    </AlertMessage>
                  </div>
                )}
                {showOutOfStockMessage && (
                  <AlertMessage icon="exclamation-outline" type="warning">
                    {meta.adjustQuantityMessage}
                  </AlertMessage>
                )}
                {item.__typename === 'GiftCardCartItem' && (
                  <>
                    <ProductInfoRow
                      testId={'recipientEmail'}
                      compact={compact}
                      name={t('recipientEmail')}
                      info={item.recipientEmail}
                    />
                    <ProductInfoRow
                      testId={'recipientName'}
                      compact={compact}
                      name={t('recipientName')}
                      info={item.recipientName}
                    />
                    <ProductInfoRow
                      testId={'senderName'}
                      compact={compact}
                      name={t('senderName')}
                      info={item.senderName}
                    />
                  </>
                )}
              </div>
              <div className="flex flex-col items-start justify-between xs:items-end md:(flex-col justify-start space-x-3) xs:flex-row-reverse">
                {(isOnlineOrPurchased ||
                  item.__typename === 'GiftCardCartItem') && (
                  <p className="flex flex-col md:mb-5 text-left xs:text-right">
                    <ProductPrice
                      price={{
                        amount: totalProductPrice,
                        salePrice,
                        promotionPrice,
                        personalizationPrice,
                      }}
                      compact={!!compact}
                    />
                  </p>
                )}
                <div className="flex flex-row mt-4 md:mt-0">
                  {item.__typename === 'ProductCartItem' &&
                    allowUpdate &&
                    !isOffline && (
                      <Button
                        dataTestId="edit-product-button"
                        variant="ghost"
                        onClick={onEdit}
                        label={`Edit ${meta.name}`}
                        icon="pencil"
                        mode="icon"
                      />
                    )}
                  {allowRemove && (
                    <Button
                      data-test-id="remove-product-button"
                      disabled={removing || updating}
                      variant="ghost"
                      onClick={onRemove}
                      label={`Remove ${meta.name}`}
                      icon="trash"
                      mode="icon"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShoppingCartItemPrimaryNested;
